// Liste des marchés municipaux : nom, code, arrondissement, emplacements et occupation du jour,
// périodicité par défaut, état ; création d'un marché par le responsable des marchés.
import { CODES_PERIODICITES, schemaCreationMarche } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Dialogue, EtatVide, Filtres, Lien, LienBouton, PuceEtat, Selection, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { Plus, Store } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { creerMarche, INVALIDER_MARCHES, useMarches } from './api';
import { LIBELLES_PERIODICITE } from './logique';
import type { MarcheResume, PeriodiciteRedevance } from './types';
import commun from '../../commun/commun.module.css';

function FormulaireMarche({ ouvert, onFermer }: { ouvert: boolean; onFermer: () => void }) {
  const naviguer = useNavigate();
  const [valeurs, setValeurs] = useState({ nom: '', adresse: '', mode: 'couvert', longitude: '', latitude: '', periodicite_defaut: 'mensuelle' as PeriodiciteRedevance });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const action = useAction({
    executer: () => {
      const position = valeurs.mode === 'ciel_ouvert'
        ? { longitude: valeurs.longitude.trim() ? Number(valeurs.longitude) : NaN, latitude: valeurs.latitude.trim() ? Number(valeurs.latitude) : NaN }
        : undefined;
      const corps = schemaCreationMarche.safeParse({ nom: valeurs.nom, adresse: valeurs.adresse || undefined, mode: valeurs.mode, position, periodicite_defaut: valeurs.periodicite_defaut });
      if (!corps.success) {
        setErreurs(Object.fromEntries(corps.error.issues.map((i) => [String(i.path[0]), i.message])));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreurs({});
      return creerMarche(corps.data);
    },
    succes: (m) => `Marché ${m.nom} créé, code ${m.code}.`,
    invalider: INVALIDER_MARCHES,
    apres: (m) => void naviguer(`/marches/${m.id}`),
  });
  const champ = (nom: keyof typeof valeurs) => action.erreursChamps.find((e) => e.champ === nom)?.message ?? erreurs[nom];
  return (
    <Dialogue
      ouvert={ouvert}
      titre="Nouveau marché"
      description="Le code du marché est généré à partir des initiales du nom. Le plan, les zones et les emplacements se saisissent ensuite sur la fiche du marché."
      onFermer={onFermer}
      actions={
        <>
          <Bouton variante="secondaire" onClick={onFermer}>
            Annuler
          </Bouton>
          <Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>
            Créer le marché
          </Bouton>
        </>
      }
    >
      <Champ libelle="Nom" value={valeurs.nom} onChange={(e) => setValeurs((v) => ({ ...v, nom: e.target.value }))} erreur={champ('nom')} required />
      <Champ libelle="Adresse" value={valeurs.adresse} onChange={(e) => setValeurs((v) => ({ ...v, adresse: e.target.value }))} erreur={champ('adresse')} facultatif />
      <Selection libelle="Type de marché" value={valeurs.mode} options={[{ valeur: 'couvert', libelle: 'Couvert' }, { valeur: 'ciel_ouvert', libelle: 'À ciel ouvert' }]} onChange={(e) => setValeurs((v) => ({ ...v, mode: e.target.value }))} />
      {valeurs.mode === 'ciel_ouvert' ? <>
        <Champ libelle="Longitude du marché" type="number" min={-180} max={180} step="any" required value={valeurs.longitude} onChange={(e) => setValeurs((v) => ({ ...v, longitude: e.target.value }))} />
        <Champ libelle="Latitude du marché" type="number" min={-85} max={85} step="any" required value={valeurs.latitude} onChange={(e) => setValeurs((v) => ({ ...v, latitude: e.target.value }))} />
        {erreurs.position ? <Bandeau type="erreur">Renseignez les deux coordonnées du marché.</Bandeau> : null}
      </> : null}
      <Selection
        libelle="Périodicité des redevances par défaut"
        options={CODES_PERIODICITES.map((p) => ({ valeur: p, libelle: LIBELLES_PERIODICITE[p] }))}
        value={valeurs.periodicite_defaut}
        onChange={(e) => setValeurs((v) => ({ ...v, periodicite_defaut: e.target.value as PeriodiciteRedevance }))}
      />
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function PageMarches() {
  const { contexte } = useSession();
  const [filtres, setFiltres] = useFiltresPersistes('marches', { actif: '' });
  const marches = useMarches(filtres.actif ? { actif: filtres.actif } : {});
  const [creation, setCreation] = useState(false);
  const colonnes: ColonneTableau<MarcheResume>[] = [
    { id: 'nom', entete: 'Marché', enTeteLigne: true, masquable: false, valeurTri: (m) => m.nom, cellule: (m) => <Lien href={`/marches/${m.id}`}>{m.nom}</Lien> },
    { id: 'code', entete: 'Code', cellule: (m) => <span className={commun.reference}>{m.code}</span>, valeurTri: (m) => m.code },
    { id: 'arrondissement', entete: 'Arrondissement', cellule: (m) => m.arrondissement?.nom ?? 'non renseigné' },
    { id: 'emplacements', entete: 'Emplacements', numerique: true, cellule: (m) => (m.statistiques ? m.statistiques.emplacements.toLocaleString('fr-FR') : 'non renseigné'), valeurTri: (m) => m.statistiques?.emplacements ?? null },
    {
      id: 'occupation',
      entete: 'Occupés ce jour',
      numerique: true,
      cellule: (m) => (m.statistiques ? `${m.statistiques.occupes.toLocaleString('fr-FR')} sur ${m.statistiques.utilisables.toLocaleString('fr-FR')}${m.statistiques.taux_occupation === null ? '' : ` (${m.statistiques.taux_occupation.toLocaleString('fr-FR')} %)`}` : 'non renseigné'),
      valeurTri: (m) => m.statistiques?.taux_occupation ?? null,
    },
    { id: 'periodicite', entete: 'Redevances', cellule: (m) => LIBELLES_PERIODICITE[m.periodicite_defaut as PeriodiciteRedevance] ?? m.periodicite_defaut },
    { id: 'etat', entete: 'État', cellule: (m) => (m.actif ? <PuceEtat ton="ok" libelle="Ouvert" /> : <PuceEtat ton="neutre" libelle="Fermé" />), valeurTri: (m) => (m.actif ? 1 : 0) },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Marchés municipaux"
        titre="Marchés"
        description="Marchés de la collectivité, plans des emplacements, attributions et redevances."
        actions={
          <>
            <LienBouton href="/marches/attributions" variante="secondaire">
              Attributions
            </LienBouton>
            <LienBouton href="/marches/domaine-public" variante="secondaire">
              Domaine public
            </LienBouton>
            {peut(contexte, 'marche_municipal', 'creer') ? (
              <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>
                Nouveau marché
              </Bouton>
            ) : null}
          </>
        }
      />
      <Filtres
        groupes={[{ id: 'actif', libelle: 'État', options: [{ valeur: '', libelle: 'Tous' }, { valeur: 'oui', libelle: 'Ouverts' }, { valeur: 'non', libelle: 'Fermés' }] }]}
        valeurs={filtres}
        onChanger={setFiltres}
      />
      <EtatRequete chargement={marches.isPending} erreur={marches.error} onReessayer={() => void marches.refetch()} lot="L2-B" fonction="La liste des marchés">
        {(marches.data ?? []).length === 0 ? (
          <EtatVide titre="Aucun marché" icone={Store}>
            <p>Aucun marché n&apos;est enregistré dans cette collectivité{filtres.actif ? ' avec ce filtre' : ''}.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Marchés" colonnes={colonnes} lignes={marches.data ?? []} cleLigne={(m) => m.id} cleColonnes="marches" />
        )}
      </EtatRequete>
      {creation ? <FormulaireMarche ouvert={creation} onFermer={() => setCreation(false)} /> : null}
    </>
  );
}
